"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Map as MapIcon, Navigation2, CircleCheck } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const locations = [{
  name: "The Crossings",
  description: "Comprehensive home inspections for family residences."
}, {
  name: "Hammocks",
  description: "Detailed property assessments and mold testing services."
}, {
  name: "Kendall West",
  description: "Expert wind mitigation and 4-point insurance inspections."
}, {
  name: "Country Walk",
  description: "Full-scale structural and sewer camera inspections."
}, {
  name: "Pinecrest & Palmetto",
  description: "Premium inspection services for luxury estates."
}];
export default function ServiceAreasListed() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="243745" id="service-areas" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={sectionRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="flex flex-col space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors">
                <MapIcon className="w-4 h-4 mr-2" />
                South Florida Coverage
              </Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight text-balance text-secondary">
                Kendall Neighborhoods We Serve
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl text-balance font-default">
                Our certified inspectors provide thorough property evaluations throughout Kendall and surrounding South Florida communities, ensuring peace of mind for every homeowner.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {locations.map((location, index) => <Card key={location.name} data-index={index} className="bg-card border-border text-card-foreground hover:border-primary/50 hover:shadow-md transition-all duration-300">
                  <CardContent className="p-4 flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold font-default text-base text-secondary">
                        {location.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 font-default">
                        {location.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>

            <div className="flex items-center space-x-2 text-sm text-muted-foreground bg-muted p-4 rounded-lg border border-border w-fit font-default">
              <Navigation2 className="w-4 h-4 text-primary" />
              <span>Local inspectors ready for same-day scheduling.</span>
            </div>
          </div>

          {}
          <div className="relative w-full max-w-2xl mx-auto lg:mx-0">
            <div className="absolute -inset-4 bg-primary/5 rounded-[2rem] blur-2xl -z-10"></div>
            <div className="rounded-2xl overflow-hidden border border-border shadow-xl bg-card p-2">
              <div className="rounded-xl overflow-hidden relative">
                <AspectRatio ratio={4 / 3}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/kendall_miami_service_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/kendall_miami_service_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/kendall_miami_service_map.png-wvc-sizes" alt="Map showing service areas including The Crossings, Hammocks, and Kendall West" className="object-cover w-full h-full opacity-95 hover:opacity-100 transition-opacity duration-500" loading="lazy" />
                  
                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 via-transparent to-transparent pointer-events-none"></div>
                  
                  {}
                  <div className="absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur-sm border border-border p-4 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CircleCheck className="w-5 h-5 text-primary" />
                        <span className="font-medium text-secondary font-default">Certified Inspectors</span>
                      </div>
                      <span className="text-sm text-muted-foreground font-default">Licensed & Insured</span>
                    </div>
                  </div>
                </AspectRatio>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}